import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Drawer from '@mui/material/Drawer'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import type { WebsiteChangeSetSummary, WebsitePreviewDevice } from './types'
import { WebsitePreviewFrame, type WebsitePreviewFrameHandle } from './WebsitePreviewFrame'

type ReviewView = 'after' | 'before' | 'split'

export interface ChangeReviewPanelProps {
  changeSet: WebsiteChangeSetSummary
  device: WebsitePreviewDevice
  narrow?: boolean
  busyAction?: 'approve' | 'reject' | null
  onDeviceChange: (device: WebsitePreviewDevice) => void
  onBack: () => void
  onApprove: () => void
  onReject: () => void
  onRefine: () => void
}

/**
 * Full-screen review composition. While active this replaces ChatWorkspace's standard Site Preview
 * header and body; the integrator must not render the ordinary panel header above it.
 */
export function ChangeReviewPanel({ changeSet, device, narrow = false, busyAction = null, onDeviceChange, onBack, onApprove, onReject, onRefine }: ChangeReviewPanelProps) {
  const [view, setView] = useState<ReviewView>('after')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const beforeRef = useRef<WebsitePreviewFrameHandle>(null)
  const afterRef = useRef<WebsitePreviewFrameHandle>(null)
  const viewportByFrame = useRef<Record<'before' | 'after', { scrollX: number; scrollY: number } | null>>({ before: null, after: null })
  const effectiveView = narrow && view === 'split' ? 'after' : view
  const compactButtonSx = {
    height: 30,
    px: 1.25,
    borderRadius: '7px',
    fontSize: '0.68rem',
    fontWeight: 600,
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
  } as const
  const ids = changeSet.affectedTargets.map(target => target.targetId)
  const frame = (kind: 'before' | 'after') => (
    <WebsitePreviewFrame
      ref={kind === 'before' ? beforeRef : afterRef}
      html={kind === 'before' ? changeSet.beforeHtml : changeSet.afterHtml}
      siteId={changeSet.siteId}
      revision={changeSet.baseRevision}
      device={device}
      readOnly
      highlightedTargetIds={ids}
      title={`${kind === 'before' ? 'Before' : 'After'} preview`}
      onBridgeMessage={message => {
        if (effectiveView !== 'split' || message.type !== 'viewport-updated') return
        const previous = viewportByFrame.current[kind]
        if (previous && Math.abs(previous.scrollX - message.viewport.scrollX) < 1 && Math.abs(previous.scrollY - message.viewport.scrollY) < 1) return
        viewportByFrame.current[kind] = message.viewport
        const peerKind = kind === 'before' ? 'after' : 'before'
        const peerViewport = viewportByFrame.current[peerKind]
        if (peerViewport && Math.abs(peerViewport.scrollX - message.viewport.scrollX) < 1 && Math.abs(peerViewport.scrollY - message.viewport.scrollY) < 1) return
        viewportByFrame.current[peerKind] = message.viewport
        const peer = kind === 'before' ? afterRef.current : beforeRef.current
        peer?.postCommand({ type: 'set-viewport', viewport: message.viewport })
      }}
    />
  )
  const details = (
    <Box sx={{ p: 2 }}>
      <Chip size="small" color="primary" variant="outlined" label="Ready for review" sx={{ mb: 1.5 }} />
      <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.5 }}>{changeSet.summary}</Typography>
      <Typography sx={{ mt: 2, mb: 0.75, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Changes</Typography>
      {changeSet.changes.map((change, index) => (
        <Box key={`${change.targetId ?? 'change'}-${index}`} sx={{ display: 'flex', gap: 1, py: 0.75 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{index + 1}.</Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{change.summary}</Typography>
            {(change.before !== undefined || change.after !== undefined) && (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>
                {change.before ?? '—'} → <Box component="span" sx={{ color: 'text.primary' }}>{change.after ?? '—'}</Box>
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      <Typography sx={{ mt: 2, mb: 0.75, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Affected elements</Typography>
      {changeSet.affectedTargets.map(target => <Typography key={target.targetId} sx={{ fontSize: '0.78rem', py: 0.4 }}><Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>{target.ordinal}.</Box>{target.label}</Typography>)}
      {changeSet.warnings.map((warning, index) => <Alert key={index} severity="warning" sx={{ mt: 1, fontSize: '0.72rem' }}>{warning}</Alert>)}
    </Box>
  )
  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title="Back to chat"><IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary' }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.dark', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{changeSet.title}</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Proposed from version {changeSet.baseRevision + 1}</Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={effectiveView}
          onChange={(_event, nextView: ReviewView | null) => { if (nextView) setView(nextView) }}
          aria-label="Comparison view"
          sx={{
            height: 30,
            '& .MuiToggleButton-root': {
              px: 1, color: 'text.secondary', borderColor: 'divider',
              fontSize: '0.65rem', fontWeight: 600, lineHeight: 1,
              textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none',
            },
            '& .MuiToggleButton-root.Mui-selected': {
              color: 'primary.dark', bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.selected' },
            },
          }}
        >
          <ToggleButton value="after">After</ToggleButton>
          <ToggleButton value="before">Before</ToggleButton>
          {!narrow && <ToggleButton value="split">Split</ToggleButton>}
        </ToggleButtonGroup>
        <Tooltip title="Desktop"><IconButton size="small" onClick={() => onDeviceChange('desktop')} sx={{ color: device === 'desktop' ? 'primary.main' : 'text.secondary' }}><DesktopWindowsIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        <Tooltip title="Mobile"><IconButton size="small" onClick={() => onDeviceChange('mobile')} sx={{ color: device === 'mobile' ? 'primary.main' : 'text.secondary' }}><PhoneIphoneIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        {narrow && <Button size="small" variant="outlined" onClick={() => setDetailsOpen(true)} sx={compactButtonSx}>Details</Button>}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: narrow ? 'column' : 'row' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
          {effectiveView === 'split' ? <><Box sx={{ width: '50%', borderRight: 1, borderColor: 'divider' }}>{frame('before')}</Box><Box sx={{ width: '50%' }}>{frame('after')}</Box></> : frame(effectiveView)}
        </Box>
        {!narrow && <Box sx={{ width: 320, overflowY: 'auto', borderLeft: 1, borderColor: 'divider' }}>{details}</Box>}
      </Box>
      <Drawer anchor="bottom" open={narrow && detailsOpen} onClose={() => setDetailsOpen(false)} PaperProps={{ sx: { maxHeight: '72vh', borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}>
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1.25 }} />
        <Box sx={{ overflowY: 'auto' }}>{details}</Box>
      </Drawer>
      <Box sx={{ display: 'flex', flexWrap: narrow ? 'wrap' : 'nowrap', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography sx={{ flex: narrow ? '1 0 100%' : 1, fontSize: '0.72rem', color: 'text.secondary' }}>Approving creates one new immutable version.</Typography>
        <Button size="small" color="inherit" disabled={!!busyAction} onClick={onReject} sx={{ ...compactButtonSx, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>{busyAction === 'reject' && <CircularProgress size={14} sx={{ mr: 1 }} />}Reject</Button>
        <Button size="small" variant="outlined" disabled={!!busyAction} onClick={onRefine} sx={compactButtonSx}>Ask Ros to refine</Button>
        <Button size="small" variant="contained" disableElevation disabled={!!busyAction} onClick={onApprove} sx={compactButtonSx}>{busyAction === 'approve' && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}Approve changes</Button>
      </Box>
    </Box>
  )
}
